import { useState, useEffect, useRef, useCallback } from "react";

export const useRefreshTimer = (
  intervalSeconds: number,
  refreshFunction: (isRefresh?: boolean) => Promise<void>,
) => {
  const [isRunning, setIsRunning] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshFunctionRef = useRef(refreshFunction);
  const isRefreshingRef = useRef(false);
  const intervalSecondsRef = useRef(intervalSeconds);
  const secondsRef = useRef(intervalSeconds);

  useEffect(() => {
    refreshFunctionRef.current = refreshFunction;
  }, [refreshFunction]);

  useEffect(() => {
    intervalSecondsRef.current = intervalSeconds;
  }, [intervalSeconds]);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  const executeRefresh = useCallback(async () => {
    if (!isRefreshingRef.current) {
      setIsRefreshing(true);
      try {
        await refreshFunctionRef.current(true); // Pass true to indicate this is a refresh
      } catch (error) {
        /* Error handled silently */
      } finally {
        setIsRefreshing(false);
        // Reset timer after refresh completes
        secondsRef.current = intervalSecondsRef.current;
      }
    }
  }, []);

  const pause = useCallback(() => {
    setTimeout(() => {
      setIsRunning(false);
    }, 10);
  }, []);

  const resume = useCallback(() => {
    // Make immediate call when resuming
    refreshFunctionRef.current?.(true);
    setTimeout(() => {
      setIsRunning(true);
    }, 10);
  }, []);

  const restart = useCallback(() => {
    secondsRef.current = intervalSecondsRef.current;
    setIsRunning(true);
  }, []);

  useEffect(() => {
    if (isRunning) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      timerRef.current = setInterval(() => {
        secondsRef.current = secondsRef.current - 1;

        if (secondsRef.current <= 0) {
          executeRefresh();
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning]);

  return {
    getSeconds: () => secondsRef.current,
    isRunning,
    isRefreshing,
    pause,
    resume,
    restart,
    executeRefresh,
  };
};
